/**
 * Camera framing for a selected satellite: position the camera so BOTH the
 * (exaggerated-altitude) satellite pin and its true-scale ground footprint are
 * in view. Uses a tilt-invariant bounding-sphere fit centred at the content
 * mid-height — correct across the full orbital range (LEO → GEO), unlike a flat
 * zoom-to-point. No regex; all angle math in radians via DEG.
 */

export type { CoverageFrame } from './coverage-frame';
import type { CoverageFrame } from './coverage-frame';

/** Degrees → radians. */
const DEG = Math.PI / 180;

/**
 * Assumed vertical FOV (deg) of the 3D camera. Map3DElement does not expose its
 * FOV, so this is deliberately conservative — a smaller value pulls the camera
 * back, which is the safe direction (content never clips).
 */
const FRAME_FOV_DEG = 30;

/** Extra margin so content does not sit flush against the frame edge. */
const FRAME_MARGIN = 1.4;

/** Range floor (m) — never closer than this. */
const MIN_RANGE = 1_000;

/** Range ceiling (m) — large enough for GEO at the 3× display shell (~285,000 km). */
const MAX_RANGE = 400_000_000;

/** Camera tilt band (deg): shallower when the shell dwarfs the disk, steeper for wide disks. */
const TILT_MIN = 30;
const TILT_MAX = 60;

/** 2D fitBounds padding (px). */
const FIT_PADDING_PX = 64;

/** Longitude span (deg) beyond which the ring straddles the antimeridian. */
const ANTIMERIDIAN_SPAN_DEG = 180;

/** Latitude span (deg) beyond which the disk is too large for a useful fitBounds. */
const OVERSIZE_LAT_SPAN_DEG = 120;

/** Km per degree of latitude (mean), for the 2D fallback zoom. */
const KM_PER_DEG = 111.32;

/** Empirical constant mapping a disk's angular radius to a fitting 2D zoom. */
const ZOOM_FIT_CONSTANT = 491;

/**
 * Frame a satellite + footprint on the 3D map. The content spans z=0 (ground
 * ring) to z=shellAlt (pin); centring the look-at at shellAlt/2 makes the
 * bounding sphere of radius hypot(groundRadius, shellAlt/2) symmetric about the
 * view axis, so the fit holds at any tilt.
 */
export function frame3D(map3d: google.maps.maps3d.Map3DElement, frame: CoverageFrame): void {
  const centerAltitude = frame.shellAltMeters / 2;
  const contentRadius = Math.hypot(frame.groundRadiusMeters, frame.shellAltMeters / 2);
  const range = clampRange((FRAME_MARGIN * contentRadius) / Math.tan((FRAME_FOV_DEG * DEG) / 2));
  map3d.flyCameraTo({
    endCamera: {
      center: { lat: frame.lat, lng: frame.lng, altitude: centerAltitude },
      range,
      tilt: coverageTilt(frame),
      heading: 0,
    },
    durationMillis: 1500,
  });
}

/** Frame the footprint on the 2D map, guarding antimeridian/pole/oversize disks. */
export function frame2D(map: google.maps.Map, frame: CoverageFrame): void {
  if (isDegenerateRing(frame.ring)) {
    map.panTo({ lat: frame.lat, lng: frame.lng });
    map.setZoom(zoomForGroundRadius(frame.groundRadiusMeters));
    return;
  }
  const bounds = new google.maps.LatLngBounds();
  bounds.extend({ lat: frame.lat, lng: frame.lng });
  for (const point of frame.ring) bounds.extend(point);
  map.fitBounds(bounds, FIT_PADDING_PX);
}

/** Clamp a camera range to the supported framing band. */
function clampRange(range: number): number {
  return Math.min(MAX_RANGE, Math.max(MIN_RANGE, range));
}

/** Oblique tilt: shallower when the exaggerated shell dwarfs the disk, steeper for wide disks. */
function coverageTilt(frame: CoverageFrame): number {
  const ratio = frame.groundRadiusMeters / (frame.groundRadiusMeters + frame.shellAltMeters);
  return TILT_MIN + (TILT_MAX - TILT_MIN) * ratio;
}

/** Whether the ring crosses the antimeridian or wraps a pole (fitBounds would balloon). */
function isDegenerateRing(ring: google.maps.LatLngLiteral[]): boolean {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const point of ring) {
    minLon = Math.min(minLon, point.lng);
    maxLon = Math.max(maxLon, point.lng);
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
  }
  return maxLon - minLon > ANTIMERIDIAN_SPAN_DEG || maxLat - minLat > OVERSIZE_LAT_SPAN_DEG;
}

/** Approximate 2D zoom that frames a disk of the given ground radius (degenerate fallback). */
function zoomForGroundRadius(groundRadiusMeters: number): number {
  const angularRadiusDeg = Math.max(0.05, groundRadiusMeters / 1000 / KM_PER_DEG);
  const zoom = Math.log2(ZOOM_FIT_CONSTANT / angularRadiusDeg);
  return Math.min(18, Math.max(2, Math.round(zoom)));
}
